/**
 * AddRudderstackAccountDrawer — create the RudderStack account a destination needs, without leaving
 * Lookout (spec §5). Two screens, mirroring the warehouse add-account drawer:
 *   1. Type — one card per account definition the caller's scope offers. The offered set is always
 *      destinations the activation registry names (scope-reduction addendum) — there is no
 *      source/destination split to control any more.
 *   2. Configure — for an OAuth definition Lookout can relay: an account name and
 *      "Connect with <platform>"; for one it cannot: the RudderStack link (spec §4.5); for a
 *      key-based one: the schema-driven `AccountForm`.
 *
 * ## Two hosts, one body (Task 7)
 *
 * {@link AddAccountBody} renders those two SCREENS and nothing else — no `Sheet`, no header, no
 * primary button. It takes its step from the HOST and reports what the host's own chrome should show
 * ({@link AddAccountChrome}), because the two hosts draw that chrome in different places:
 *
 *  - this drawer puts it in its `WizardHeader`, for `SettingsAccounts`, which still mounts
 *    a `Sheet` of its own (the other two hosts, `AccountPickerSheet` and `AccountManagement`, are
 *    deleted — the wizard's account step is an inline field now);
 *  - the activation wizard renders the same body as two of its own steps and puts the primary in the
 *    sheet's shared footer — where a single wizard-level `doneLabel` could never say both "Connect"
 *    (the sub-flow's last step) and "Enable sync" (the wizard's).
 *
 * Reporting the primary rather than rendering it is what makes that possible, and it is why the step
 * is a prop: the wizard's tree owns navigation there, so deriving the screen from `selected` would
 * let the two disagree.
 */
import type { RouterClient } from '@orpc/server';
import type { ActivationDestinationType } from '@server/activation-destinations';
import type { AppRouter } from '@server/rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Input } from '@/_shared/components/ui/input';
import { Label } from '@/_shared/components/ui/label';
import { Sheet, SheetContent } from '@/_shared/components/ui/sheet';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { WizardFooter } from '@/_shared/components/wizard/WizardFooter';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { useRudderstackSignInAgain } from '@/_shared/hooks/useRudderstackSignInAgain';
import { useWizard } from '@/_shared/hooks/useWizard';
import { isOrpcConflictError, orpcErrorMessage } from '@/_shared/lib/orpc-errors';
import { ADD_ACCOUNT_TREE, type AddAccountStepKey } from '@/settings/lib/addAccountTree';

import { WizardHeader } from '../warehouseAccount/addAccount/WizardHeader';
import { AccountForm, type AccountFormHandle } from './AccountForm';
import { AccountTypeRow } from './AccountTypeRow';
import { DestinationBadge } from './DestinationBadge';
import { useRudderstackOAuth } from './useRudderstackOAuth';

/** One definition as `listDefinitions` returns it — derived from the router, never re-declared. */
type Definition = Awaited<
  ReturnType<RouterClient<AppRouter>['settings']['rudderstackAccounts']['listDefinitions']>
>['definitions'][number];

/** The account definition a host holds while the body is on its configure screen. */
export type AddAccountDefinition = Definition;

/**
 * What the HOST should render for the body's current screen.
 *
 * `primary` is null where the screen has no primary at all: the type list, and the "create this
 * account in RudderStack" screen, whose only action is a link the body draws itself. `onClick` is
 * stable across renders and reads the latest handler, so a host may hold this object in state
 * without capturing a stale `name` or a stale mutation.
 */
export interface AddAccountChrome {
  primary: { label: string; disabled: boolean; loading: boolean; onClick: () => void } | null;
  /** False while a consent popup is live: leaving the screen would orphan the flow. */
  canLeave: boolean;
}

interface AddRudderstackAccountDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Narrows the offered definitions to one activation type's; omit for the whole registry. */
  destinationType?: ActivationDestinationType;
  /**
   * Narrows the offered definitions to exactly these names — the alternative to `destinationType` for
   * a caller that already resolved its own scope generically (the activation wizard's account step,
   * spec §7, which takes `accountDefinitionNames` rather than a six-type registry key). Ignored when
   * `destinationType` is also given; `listDefinitions` itself supports both narrowings, so this only
   * threads the one this drawer did not have a parameter for yet.
   */
  accountDefinitionNames?: string[];
  surface: 'activation' | 'settings';
  onCreated?: (account: { accountId: string; name: string; accountDefinitionName: string }) => void;
}

/**
 * The sheet itself. Everything stateful lives below it, inside `SheetContent`, which the sheet
 * unmounts when it closes — so a re-open starts at step 1 and the OAuth hook's unmount teardown
 * cancels any timer.
 */
export function AddRudderstackAccountDrawer({
  open,
  onOpenChange,
  ...body
}: AddRudderstackAccountDrawerProps): React.JSX.Element {
  // While a popup is open on the provider, dismissing the sheet would unmount the opener and orphan
  // the flow (PR #731 review): block Escape / outside-click / close and keep only Cancel.
  //
  // Tracked in a REF, not state. The body mirrors its own inFlight up via `onInFlightChange` in an
  // effect, and on completion that mirror effect and the body's own close-on-done effect run in the
  // SAME commit, in declaration order (mirror first, close second — see the body below). Mirroring
  // through `setState` would only *schedule* a re-render, so the close call would still run against
  // the `guardedOpenChange` closure captured at the PREVIOUS render — the one where inFlight was
  // still true — and silently discard the close. A ref write is synchronous, so by the time the
  // close-on-done effect calls `guardedOpenChange`, the mirror effect has already flipped it to
  // false in the same pass. The guard itself is unchanged: a user dismissal while inFlight is true
  // is still blocked. See oauth-create-close-fix-brief.md.
  //
  // This is ALSO why `onInFlightChange` survives the Task 7 split rather than being folded into
  // `onChromeChange`: chrome is render state, and the guard cannot wait a render.
  const inFlightRef = useRef(false);
  const handleInFlightChange = useCallback((next: boolean): void => {
    inFlightRef.current = next;
  }, []);
  const guardedOpenChange = (next: boolean): void => {
    if (!next && inFlightRef.current) return;
    onOpenChange(next);
  };
  const block = (e: Event): void => {
    if (inFlightRef.current) e.preventDefault();
  };
  return (
    <Sheet open={open} onOpenChange={guardedOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="w-full gap-0 sm:max-w-drawer"
        onEscapeKeyDown={block}
        onPointerDownOutside={block}
        onInteractOutside={block}
      >
        <AddRudderstackAccountScreen
          onOpenChange={guardedOpenChange}
          onInFlightChange={handleInFlightChange}
          {...body}
        />
      </SheetContent>
    </Sheet>
  );
}

/**
 * The drawer's own chrome around the shared body: the wizard header, its Back, and the reported
 * primary in the header's `actions` slot.
 *
 * It holds `selected` — and holding it INSIDE `SheetContent` is deliberate, because that is what the
 * Sheet unmounts on close: keeping it in the wrapper above would re-open the drawer on the configure
 * screen of whatever type was last chosen.
 */
function AddRudderstackAccountScreen({
  onOpenChange,
  onInFlightChange,
  onCreated,
  ...body
}: Omit<AddRudderstackAccountDrawerProps, 'open'> & {
  onInFlightChange: (inFlight: boolean) => void;
}): React.JSX.Element {
  const wiz = useWizard<AddAccountStepKey, never>({ tree: ADD_ACCOUNT_TREE, initialStep: 'select-account-type' });
  const [selected, setSelected] = useState<AddAccountDefinition | null>(null);
  const [chrome, setChrome] = useState<AddAccountChrome | null>(null);
  const { goNext, goBack } = wiz;
  const step: 'type' | 'configure' = wiz.stepKey === 'configure' ? 'configure' : 'type';
  const primary = chrome?.primary ?? null;
  const canLeave = chrome?.canLeave ?? true;

  /**
   * The screen's name, shown where "Step 2 of 2: Configure" used to be.
   *
   * The counter is gone because there is nowhere honest for it to come from — `WizardTree` branches,
   * so "of N" has no single answer — and the title is what every other wizard shell here renders.
   *
   * The override is the tree's own documented division of labour: `configure` covers three screens
   * and only the schema-driven form is truthfully called "Configure", so the drawer names it once it
   * knows the definition. Same shape as a body reporting its own primary in place of `doneLabel`.
   */
  const stepTitle = selected && selected.lookoutSupport !== 'form' ? 'Connect' : wiz.step?.title;

  /**
   * Picking a definition ASKS the host to advance — the body's own contract — so the drawer answers
   * by moving the tree, not by letting the step fall out of `selected`. Clearing it (the body's
   * teardown effect on arriving back at the type screen) must NOT move anything, or Back would walk
   * the tree twice.
   */
  const handleSelectedChange = useCallback(
    (definition: AddAccountDefinition | null): void => {
      setSelected(definition);
      if (definition) goNext();
    },
    [goNext],
  );

  const handleCreated = useCallback(
    (account: { accountId: string; name: string; accountDefinitionName: string }): void => {
      onCreated?.(account);
      onOpenChange(false);
    },
    [onCreated, onOpenChange],
  );

  return (
    <>
      <WizardHeader
        title="Add account"
        badge={
          selected ? (
            <DestinationBadge destinationType={selected.destinationType} label={selected.destinationDisplayName} />
          ) : null
        }
        stepLabel={stepTitle}
      />
      {/* The scroll box belongs to the HOST: the activation wizard already has one of its own, and a
          body that brought its own would nest two. */}
      <div className="flex-1 overflow-y-auto p-4">
        <AddAccountBody
          {...body}
          step={step}
          selected={selected}
          onSelectedChange={handleSelectedChange}
          onChromeChange={setChrome}
          onInFlightChange={onInFlightChange}
          onCreated={handleCreated}
        />
      </div>
      {/* Back and the primary moved out of the header, which had the title, the destination badge,
          Back, Save and the close all competing for one row — "Add RudderStack account" wrapped onto
          two lines at the drawer's own width.

          Back is now DISABLED with its reason while a consent popup is live, where it used to vanish
          (`step === 'configure' && canLeave`). Disable-with-a-reason is the repo's rule (ui skill
          10), and it is what the activation wizard already does on the same condition — a control
          that disappears mid-flow reads as a bug, and there is nothing else on this screen to
          explain it. */}
      <WizardFooter
        back={
          wiz.footer.showBack
            ? {
                label: wiz.footer.backLabel,
                onClick: goBack,
                disabled: !canLeave,
                disabledReason: canLeave ? null : 'Finish or cancel the sign-in first',
              }
            : null
        }
        primary={
          // `showPrimary` AND a reported primary: the `external` screen is a `form` step whose only
          // action is a link the body draws itself, so it reports none and the footer shows only Back.
          wiz.footer.showPrimary && primary
            ? { label: primary.label, onClick: primary.onClick, disabled: primary.disabled, loading: primary.loading }
            : null
        }
      />
    </>
  );
}

export interface AddAccountBodyProps {
  /** Narrows the offered definitions to one activation type's; omit for the whole registry. */
  destinationType?: ActivationDestinationType;
  /** Narrows the offered definitions to exactly these names. */
  accountDefinitionNames?: string[];
  surface: 'activation' | 'settings';
  onCreated?: (account: { accountId: string; name: string; accountDefinitionName: string }) => void;
  /**
   * Which screen to render. The HOST's step — the drawer derives it from its own `selected`, the
   * activation wizard reads it off its step tree. Never derived here, so the wizard's tree stays the
   * one authority on where the user is.
   */
  step: 'type' | 'configure';
  /** The chosen definition. Controlled: picking one ASKS the host to advance. */
  selected: AddAccountDefinition | null;
  onSelectedChange: (definition: AddAccountDefinition | null) => void;
  /** What the host's header or footer should show for this screen. */
  onChromeChange: (chrome: AddAccountChrome) => void;
  /** Synchronous mirror of "a consent popup is live" — see the drawer's `inFlightRef`. */
  onInFlightChange: (inFlight: boolean) => void;
  /**
   * A consent page has gone live. From here on RudderStack may have created the account whatever the
   * user does next, so a host that lets them walk away has something to say about it.
   */
  onConsentStarted?: () => void;
}

export function AddAccountBody({
  destinationType,
  accountDefinitionNames,
  surface,
  onCreated,
  step,
  selected,
  onSelectedChange,
  onChromeChange,
  onInFlightChange,
  onConsentStarted,
}: AddAccountBodyProps): React.JSX.Element {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  // ACT2-1124: an ended RudderStack sign-in offers "Sign in again" on the toast.
  const signIn = useRudderstackSignInAgain();
  const definitionsQuery = useQuery(
    orpc.settings.rudderstackAccounts.listDefinitions.queryOptions({
      input: destinationType ? { destinationType } : accountDefinitionNames ? { names: accountDefinitionNames } : {},
    }),
  );
  const [name, setName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<AccountFormHandle>(null);
  const oauth = useRudderstackOAuth();
  const { reset: resetOauth, start: startOauth } = oauth;

  const inFlight = oauth.status === 'starting' || oauth.status === 'waiting';
  // FIRST, and before the completion effect below — see the drawer's `inFlightRef` note.
  useEffect(() => {
    onInFlightChange(inFlight);
    return () => onInFlightChange(false);
  }, [inFlight, onInFlightChange]);

  const definitions = useMemo(() => definitionsQuery.data?.definitions ?? [], [definitionsQuery.data]);
  const connectUrl = definitionsQuery.data?.connectInRudderstackUrl;

  // Picking a definition starts a fresh attempt: clear any banner error left over from a previous
  // one (the AccountForm instance itself unmounts/remounts on the type<->configure transition, so
  // its own field errors don't need clearing here — only this component's own formError state does).
  const selectDefinition = (d: Definition): void => {
    setFormError(null);
    onSelectedChange(d);
  };

  /**
   * Back is the HOST's move now, so the teardown that used to hang off this drawer's own Back button
   * hangs off arriving at the type screen instead — one place, whichever host moved, and it cannot be
   * forgotten by a host that draws its own Back.
   *
   * Only the OAuth flow and the selection: `formError` needs no clearing here because it is rendered
   * only on the configure screen and `selectDefinition` — the one route back onto that screen —
   * clears it.
   */
  useEffect(() => {
    if (step !== 'type') return;
    resetOauth();
    if (selected !== null) onSelectedChange(null);
  }, [step, selected, onSelectedChange, resetOauth]);

  /* eslint-disable react-hooks/refs -- the create-mutation callbacks read formRef only when the async onSuccess/onError handlers run, never during render */
  const createMutation = useMutation(
    orpc.settings.rudderstackAccounts.create.mutationOptions({
      onSuccess: (data, variables) => {
        toast.success(`Created ${variables.name}`);
        // All account-backed query keys, always (shared UI notes §11): settings, activation picker,
        // and audience detail bindings read the same accounts, so invalidating only the settings key
        // would leave a just-created account invisible there.
        void queryClient.invalidateQueries({ queryKey: orpc.settings.rudderstackAccounts.key() });
        void queryClient.invalidateQueries({ queryKey: orpc.audience.listActivationAccounts.key() });
        void queryClient.invalidateQueries({ queryKey: orpc.audience.listRudderstackWarehouseAccounts.key() });
        onCreated?.({
          accountId: data.accountId,
          name: variables.name,
          accountDefinitionName: variables.accountDefinitionName,
        });
      },
      onError: (err) => {
        if (isOrpcConflictError(err)) {
          // `orpcErrorMessage`, not `err.message`: the platform's own text now travels under
          // `data.message` because the top-level message is logged unredacted (see its doc comment).
          formRef.current?.setError('name', orpcErrorMessage(err, 'This name is already in use'));
          return;
        }
        if (signIn.toastIfReauth(err)) return;
        setFormError(orpcErrorMessage(err, 'Failed to create account'));
      },
    }),
  );
  /* eslint-enable react-hooks/refs -- end create-mutation block */

  const handleSave = async (): Promise<void> => {
    if (!selected || !formRef.current) return;
    const valid = await formRef.current.validate();
    if (!valid) return;
    setFormError(null);
    const values = formRef.current.getValues();
    try {
      await createMutation.mutateAsync({ accountDefinitionName: selected.name, ...values, surface });
    } catch {
      // onError above already updated the UI; this guard just keeps mutateAsync's rejection from
      // surfacing as an unhandled promise rejection.
    }
  };

  const canConnect =
    name.trim().length > 0 && (oauth.status === 'idle' || oauth.status === 'error' || oauth.status === 'blocked');

  /**
   * `oauth.start` opens the popup SYNCHRONOUSLY, before any await — that is what survives a popup
   * blocker — so every route to it must stay inside the user's gesture. The host's footer button
   * calls this through `onPrimaryClick` below: one extra frame on the stack, still no await.
   */
  const startConnect = (): void => {
    if (!selected || !canConnect) return;
    void startOauth({ kind: 'create', accountDefinitionName: selected.name, name: name.trim(), surface });
  };

  const runPrimary = (): void => {
    if (step !== 'configure' || !selected) return;
    if (selected.lookoutSupport === 'form') {
      void handleSave();
      return;
    }
    if (selected.lookoutSupport === 'oauth') startConnect();
  };
  /**
   * The reported `onClick` is stable, so a host may hold the chrome in state; the handler it runs is
   * this render's. Without the indirection the chrome would only be re-reported when its LABEL or
   * disabled state changed, and a handler captured two keystrokes ago would connect under the wrong
   * account name.
   */
  const runPrimaryRef = useRef(runPrimary);
  useEffect(() => {
    runPrimaryRef.current = runPrimary;
  });
  const onPrimaryClick = useCallback(() => {
    runPrimaryRef.current();
  }, []);

  const primaryLabel =
    step !== 'configure' || !selected
      ? null
      : selected.lookoutSupport === 'form'
        ? 'Save'
        : selected.lookoutSupport === 'oauth'
          ? `Connect with ${selected.destinationDisplayName}`
          : // 'external' — the only action is the link this body draws itself.
            null;
  const primaryDisabled = selected?.lookoutSupport === 'form' ? createMutation.isPending : !canConnect;
  const primaryLoading = selected?.lookoutSupport === 'form' ? createMutation.isPending : oauth.status === 'starting';

  useEffect(() => {
    onChromeChange({
      primary:
        primaryLabel === null
          ? null
          : { label: primaryLabel, disabled: primaryDisabled, loading: primaryLoading, onClick: onPrimaryClick },
      canLeave: !inFlight,
    });
  }, [onChromeChange, primaryLabel, primaryDisabled, primaryLoading, onPrimaryClick, inFlight]);

  useEffect(() => {
    if (oauth.status === 'waiting') onConsentStarted?.();
  }, [oauth.status, onConsentStarted]);

  useEffect(() => {
    if (oauth.status === 'done' && oauth.accountId && selected) {
      toast.success(`Connected ${name.trim() || selected.destinationDisplayName}`);
      // All account-backed query keys, always (shared UI notes §11), mirroring the API-key create
      // handler above. The OAuth path had no invalidation of its own — that was masked by
      // SettingsAccounts's
      // now-removed `onCreated` refetch (commit 3243f009), and this body has other hosts, so a
      // host-level fix would leave them stale.
      void queryClient.invalidateQueries({ queryKey: orpc.settings.rudderstackAccounts.key() });
      void queryClient.invalidateQueries({ queryKey: orpc.audience.listActivationAccounts.key() });
      void queryClient.invalidateQueries({ queryKey: orpc.audience.listRudderstackWarehouseAccounts.key() });
      onCreated?.({ accountId: oauth.accountId, name: name.trim(), accountDefinitionName: selected.name });
    }
    // Fire once per completed flow; the deps that matter are status/accountId.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once on completion
  }, [oauth.status, oauth.accountId]);

  return (
    <>
      {step === 'type' && (
        <div className="flex flex-col gap-2">
          {definitionsQuery.isLoading && (
            <>
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </>
          )}
          {definitionsQuery.isError && <Callout variant="error">Could not load the account types.</Callout>}
          {/* The SAME row the activation wizard's account-type step draws — see `AccountTypeRow`.
              The list itself is deliberately NOT shared: this surface offers whatever
              `listDefinitions` returns, where the wizard filters to activation destinations. */}
          {definitions.map((d) => (
            <AccountTypeRow key={d.name} definition={d} onSelect={() => selectDefinition(d)} />
          ))}
          {!definitionsQuery.isLoading && !definitionsQuery.isError && definitions.length === 0 && (
            <Callout variant="info">No account types are available for this destination.</Callout>
          )}
        </div>
      )}

      {step === 'configure' && selected?.lookoutSupport === 'form' && (
        <div className="flex flex-col gap-4">
          {formError && <Callout variant="error">{formError}</Callout>}
          <AccountForm
            ref={formRef}
            fields={selected.form.fields}
            secretFields={selected.secretFields}
            definitionName={selected.name}
          />
        </div>
      )}

      {step === 'configure' && selected?.lookoutSupport === 'external' && (
        <div className="flex flex-col gap-3">
          <Callout variant="info">
            RudderStack creates {selected.destinationDisplayName} accounts inside destination setup. Start adding a
            destination, connect the account on its settings form, then come back — you can abandon the destination.
          </Callout>
          <Button asChild variant="primary" suffixIcon={<ExternalLink className="size-4" />}>
            <a href={connectUrl ?? '#'} target="_blank" rel="noopener noreferrer">
              Create this account in RudderStack
            </a>
          </Button>
        </div>
      )}

      {step === 'configure' && selected?.lookoutSupport === 'oauth' && (
        // Still a form, though its submit button now lives in the host's chrome: the name field is
        // the only control in here, so Enter still starts the flow through implicit submission.
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            startConnect();
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rs-account-name">Account name</Label>
            <Input
              id="rs-account-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`e.g. ${selected.destinationDisplayName} — marketing`}
              disabled={oauth.status === 'waiting' || oauth.status === 'starting'}
              autoComplete="off"
            />
          </div>

          {oauth.status === 'waiting' && (
            <Callout variant="info">
              <span>
                Waiting for {selected.destinationDisplayName}… finish signing in using the window that opened.
              </span>
            </Callout>
          )}
          {oauth.status === 'blocked' && (
            <Callout variant="warning">
              <span>Your browser blocked the sign-in window. Allow popups for Lookout and try again, or </span>
              <a className="underline" href={connectUrl ?? '#'} target="_blank" rel="noopener noreferrer">
                create this account in RudderStack
              </a>
              .
            </Callout>
          )}
          {oauth.status === 'error' && oauth.error && <Callout variant="error">{oauth.error}</Callout>}

          {(oauth.status === 'waiting' || oauth.status === 'starting') && (
            <div className="flex items-center gap-2">
              <Button type="button" variant="secondary" onClick={oauth.cancel} label="Cancel" />
              {oauth.status === 'waiting' && (
                <Button type="button" variant="secondary" onClick={oauth.reopen} label="Reopen the window" />
              )}
            </div>
          )}
        </form>
      )}
    </>
  );
}
